import { forwardRef, useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { MenuItem } from '@mui/material';
import { alpha } from '@mui/material/styles';
import Check from '@mui/icons-material/Check';
import { Box } from '../../Layout/Box';
import { Chip } from '../../DataDisplay/Chip';
import { Typography } from '../../Typography';
import { Button } from '../../Button/button';
import { Select } from '../../Forms/Select';
import { Autocomplete } from '../../Forms/Autocomplete';
import type {
  AutocompleteMultipleFilterField,
  ChipGroupFilterField,
  FilterField,
  FilterFieldOption,
  FilterFieldValue,
  FilterFormProps,
  MultiChipGroupFilterField,
  MultipleSelectFilterField,
  SingleSelectFilterField,
} from './FilterForm.types';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Whether a field's value is a string array (as opposed to a single string). */
function isArrayField(field: FilterField): boolean {
  return (
    field.type === 'multiple-select' ||
    field.type === 'multi-chip-group' ||
    field.type === 'autocomplete-multiple'
  );
}

/** Shallow-compare two string arrays (order-insensitive). */
function arraysEqual(a: string[] | undefined | null, b: string[] | undefined | null): boolean {
  const arrA = a || [];
  const arrB = b || [];
  if (arrA.length !== arrB.length) return false;
  const sortedA = [...arrA].sort();
  const sortedB = [...arrB].sort();
  return sortedA.every((val, index) => val === sortedB[index]);
}

/** Return the "cleared" value for a given filter field. */
function getClearedValue(field: FilterField): FilterFieldValue {
  if (isArrayField(field)) return [];
  if (field.type === 'single-select' && field.includeAllOption) return 'all';
  return field.options[0]?.value || '';
}

/** Shallow-compare two staged value maps. */
function valuesEqual(
  a: Record<string, FilterFieldValue>,
  b: Record<string, FilterFieldValue>
): boolean {
  const keysA = Object.keys(a);
  if (keysA.length !== Object.keys(b).length) return false;
  return keysA.every((key) => {
    const valueA = a[key];
    const valueB = b[key];
    if (Array.isArray(valueA) || Array.isArray(valueB)) {
      return arraysEqual(valueA as string[], valueB as string[]);
    }
    return valueA === valueB;
  });
}

/** Build a values map from an array of fields. */
function buildValuesMap(fields: FilterField[]): Record<string, FilterFieldValue> {
  const map: Record<string, FilterFieldValue> = {};
  for (const field of fields) {
    map[field.id] = field.value;
  }
  return map;
}

/** Call each field's individual `onChange` with the given value map. */
function applyFieldValues(fields: FilterField[], values: Record<string, FilterFieldValue>) {
  for (const field of fields) {
    const value = values[field.id];
    if (field.type === 'chip-group' || field.type === 'single-select') {
      field.onChange(value as string);
    } else {
      field.onChange(value as string[]);
    }
  }
}

// ---------------------------------------------------------------------------
// Shared styles
// ---------------------------------------------------------------------------

const SELECT_LABEL_SX = {
  fontSize: 'subtitle1.fontSize',
  backgroundColor: 'background.paper',
  paddingRight: '6px',
  '&.Mui-focused': { color: 'text.primary' },
} as const;

// ---------------------------------------------------------------------------
// Field Renderers
// ---------------------------------------------------------------------------

interface ChipGroupRendererProps {
  field: ChipGroupFilterField;
  localValue: string;
  onValueChange: (fieldId: string, value: FilterFieldValue) => void;
}

function ChipGroupRenderer({ field, localValue, onValueChange }: ChipGroupRendererProps) {
  const labelId = `filter-${field.id}-label`;
  const hasSelection = field.options.some((o) => localValue === o.value);

  return (
    <Box sx={{ py: 1 }}>
      <Typography id={labelId} variant="body1">
        {field.label}
      </Typography>
      <Box role="radiogroup" aria-labelledby={labelId} display="flex" gap={1} flexWrap="wrap">
        {field.options.map((option, index) => {
          const isSelected = localValue === option.value;
          const isFocused = isSelected || (index === 0 && !hasSelection);
          return (
            <Chip
              key={option.value}
              role="radio"
              aria-checked={isSelected}
              aria-disabled={field.disabled}
              tabIndex={isFocused ? 0 : -1}
              icon={isSelected ? <Check fontSize="small" /> : undefined}
              label={option.label}
              data-testid={`filter_chip_${option.value}`}
              onClick={() => {
                if (!field.disabled) onValueChange(field.id, option.value);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  if (!field.disabled) onValueChange(field.id, option.value);
                }
              }}
              color={isSelected ? (field.disabled ? 'secondary' : 'primary') : 'default'}
              variant="filled"
              shape="pill"
              size="small"
              disabled={field.disabled}
              sx={{ cursor: field.disabled ? 'default' : 'pointer' }}
            />
          );
        })}
      </Box>
    </Box>
  );
}

interface MultiChipGroupRendererProps {
  field: MultiChipGroupFilterField;
  localValue: string[];
  onValueChange: (fieldId: string, value: FilterFieldValue) => void;
}

function MultiChipGroupRenderer({ field, localValue, onValueChange }: MultiChipGroupRendererProps) {
  const labelId = `filter-${field.id}-listbox-label`;

  const handleToggle = (optionValue: string) => {
    const isSelected = localValue.includes(optionValue);
    const newValue = isSelected
      ? localValue.filter((v) => v !== optionValue)
      : [...localValue, optionValue];
    onValueChange(field.id, newValue);
  };

  return (
    <Box sx={{ py: 1 }}>
      <Typography id={labelId} variant="body1">
        {field.label}
      </Typography>
      <Box
        role="listbox"
        aria-labelledby={labelId}
        aria-multiselectable="true"
        display="flex"
        gap={1}
        flexWrap="wrap"
      >
        {field.options.map((option, index) => {
          const isSelected = localValue.includes(option.value);
          return (
            <Chip
              key={option.value}
              role="option"
              aria-selected={isSelected}
              aria-disabled={field.disabled}
              tabIndex={index === 0 ? 0 : -1}
              icon={isSelected ? <Check fontSize="small" /> : undefined}
              label={option.label}
              onClick={() => {
                if (!field.disabled) handleToggle(option.value);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  if (!field.disabled) handleToggle(option.value);
                }
              }}
              color={isSelected ? (field.disabled ? 'secondary' : 'primary') : 'default'}
              variant="filled"
              shape="pill"
              size="small"
              disabled={field.disabled}
              sx={{ cursor: field.disabled ? 'default' : 'pointer' }}
              data-testid={`filter_chip_${option.value}`}
            />
          );
        })}
      </Box>
    </Box>
  );
}

interface SingleSelectRendererProps {
  field: SingleSelectFilterField;
  localValue: string;
  onValueChange: (fieldId: string, value: FilterFieldValue) => void;
  disabled: boolean;
}

function SingleSelectRenderer({
  field,
  localValue,
  onValueChange,
  disabled,
}: SingleSelectRendererProps) {
  return (
    <Box sx={{ pt: 2, pb: 1 }}>
      <Select
        value={localValue}
        onChange={(value) => onValueChange(field.id, value)}
        label={field.label}
        native={false}
        sx={SELECT_LABEL_SX}
        disabled={disabled}
      >
        {field.includeAllOption && <MenuItem value="all">{field.allOptionLabel || 'All'}</MenuItem>}
        {field.options.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
    </Box>
  );
}

interface MultipleSelectRendererProps {
  field: MultipleSelectFilterField;
  localValue: string[];
  onValueChange: (fieldId: string, value: FilterFieldValue) => void;
  disabled: boolean;
  isOpen: boolean;
  onOpenChange: (fieldId: string, open: boolean) => void;
}

function MultipleSelectRenderer({
  field,
  localValue,
  onValueChange,
  disabled,
  isOpen,
  onOpenChange,
}: MultipleSelectRendererProps) {
  return (
    <Box sx={{ pt: 2, pb: 1 }}>
      <Select
        value={localValue}
        onChange={(value) => {
          onValueChange(field.id, value as string[]);
          onOpenChange(field.id, false);
        }}
        label={field.label}
        native={false}
        sx={SELECT_LABEL_SX}
        disabled={disabled}
        SelectProps={{
          multiple: true,
          open: isOpen,
          onOpen: () => onOpenChange(field.id, true),
          onClose: () => onOpenChange(field.id, false),
          renderValue: (selected) => {
            const values = Array.isArray(selected) ? selected : selected ? [selected] : [];
            if (values.length === 0) {
              return field.emptyValueLabel || 'All';
            }
            return values.join(', ');
          },
        }}
      >
        {field.options.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
    </Box>
  );
}

interface AutocompleteMultipleRendererProps {
  field: AutocompleteMultipleFilterField;
  localValue: string[];
  onValueChange: (fieldId: string, value: FilterFieldValue) => void;
  disabled: boolean;
}

function AutocompleteMultipleRenderer({
  field,
  localValue,
  onValueChange,
  disabled,
}: AutocompleteMultipleRendererProps) {
  const selectedOptions = field.options.filter((option) => localValue.includes(option.value));

  return (
    <Box sx={{ pt: 2, pb: 1 }}>
      <Autocomplete<FilterFieldOption, true>
        multiple
        options={field.options}
        value={selectedOptions}
        onChange={(_, value) =>
          onValueChange(
            field.id,
            value.map((option) => option.value)
          )
        }
        getOptionLabel={(option) => option.label}
        isOptionEqualToValue={(option, value) => option.value === value.value}
        label={field.label}
        disabled={disabled}
        disableCloseOnSelect
        blurOnSelect={false}
        textFieldProps={{
          placeholder: localValue.length === 0 ? field.emptyValueLabel || 'All' : undefined,
          sx: SELECT_LABEL_SX,
        }}
        data-testid={`filter_autocomplete_${field.id}`}
      />
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * FilterForm — A generic, configuration-driven filter form component.
 *
 * Displays configurable filter fields (chip groups, single/multi selects) with
 * Apply and Clear buttons. Maintains local state independently from the parent,
 * enabling users to make changes and cancel by closing without applying.
 *
 * Supports five filter field types:
 * - **chip-group** — single-selection chips with radio semantics
 * - **multi-chip-group** — multi-selection chips with listbox semantics
 * - **single-select** — dropdown for single selection
 * - **multiple-select** — dropdown for multiple selection
 * - **autocomplete-multiple** — searchable autocomplete for multiple selection
 *
 * Can be used inside a Popover, Drawer, Sidebar, or any other container.
 *
 * @example
 * ```tsx
 * <FilterForm
 *   fields={filterFields}
 *   onApply={handleApply}
 *   onClear={handleClear}
 *   applyButtonLabel="Apply"
 *   clearButtonLabel="Reset"
 * />
 * ```
 */
export const FilterForm = forwardRef<HTMLDivElement, FilterFormProps>(function FilterForm(
  {
    isOpen = true,
    fields,
    onClear,
    onApply,
    isLoadingOptions = false,
    optionsError,
    optionsErrorMessage,
    applyButtonLabel = 'Apply Filters',
    clearButtonLabel = 'Clear',
    containerSx,
    hideActions = false,
    onValuesChange,
  },
  ref
) {
  const initialValues = useMemo(() => buildValuesMap(fields), [fields]);

  const [localValues, setLocalValues] = useState<Record<string, FilterFieldValue>>(initialValues);
  const [multipleSelectOpen, setMultipleSelectOpen] = useState<Record<string, boolean>>({});

  // Sync local state with props when form opens
  useEffect(() => {
    if (isOpen) {
      setLocalValues(buildValuesMap(fields));
      setMultipleSelectOpen({});
    }
  }, [isOpen, fields]);

  const onValuesChangeRef = useRef(onValuesChange);
  useEffect(() => {
    onValuesChangeRef.current = onValuesChange;
  });

  // Emit only on a real content change. Most parents rebuild `fields` every
  // render, so the sync effect above produces a fresh object with identical
  // contents constantly; forwarding those can feed back into another sync.
  const lastEmittedValuesRef = useRef<Record<string, FilterFieldValue> | null>(null);
  useEffect(() => {
    if (lastEmittedValuesRef.current && valuesEqual(lastEmittedValuesRef.current, localValues)) {
      return;
    }
    lastEmittedValuesRef.current = localValues;
    onValuesChangeRef.current?.(localValues);
  }, [localValues]);

  const handleFieldChange = useCallback((fieldId: string, value: FilterFieldValue) => {
    setLocalValues((prev) => ({ ...prev, [fieldId]: value }));
  }, []);

  const handleSelectOpenChange = useCallback((fieldId: string, open: boolean) => {
    setMultipleSelectOpen((prev) => ({ ...prev, [fieldId]: open }));
  }, []);

  const handleApply = useCallback(() => {
    if (onApply) {
      onApply(localValues);
    } else {
      applyFieldValues(fields, localValues);
    }
  }, [onApply, localValues, fields]);

  const handleClear = useCallback(() => {
    const clearedValues: Record<string, FilterFieldValue> = {};
    for (const field of fields) {
      clearedValues[field.id] = getClearedValue(field);
    }
    setLocalValues(clearedValues);

    if (onClear) {
      onClear();
    } else {
      applyFieldValues(fields, clearedValues);
    }
  }, [onClear, fields]);

  // Check if any field has changes
  const hasChanges = fields.some((field) => {
    const localValue = localValues[field.id];
    if (isArrayField(field)) {
      return !arraysEqual(localValue as string[], field.value as string[]);
    }
    return localValue !== field.value;
  });

  const renderField = (field: FilterField) => {
    switch (field.type) {
      case 'chip-group':
        return (
          <ChipGroupRenderer
            key={field.id}
            field={field}
            localValue={localValues[field.id] as string}
            onValueChange={handleFieldChange}
          />
        );
      case 'multi-chip-group':
        return (
          <MultiChipGroupRenderer
            key={field.id}
            field={field}
            localValue={(localValues[field.id] as string[]) || []}
            onValueChange={handleFieldChange}
          />
        );
      case 'single-select':
        return (
          <SingleSelectRenderer
            key={field.id}
            field={field}
            localValue={localValues[field.id] as string}
            onValueChange={handleFieldChange}
            disabled={field.disabled || isLoadingOptions}
          />
        );
      case 'multiple-select':
        return (
          <MultipleSelectRenderer
            key={field.id}
            field={field}
            localValue={localValues[field.id] as string[]}
            onValueChange={handleFieldChange}
            disabled={field.disabled || isLoadingOptions}
            isOpen={multipleSelectOpen[field.id] || false}
            onOpenChange={handleSelectOpenChange}
          />
        );
      case 'autocomplete-multiple':
        return (
          <AutocompleteMultipleRenderer
            key={field.id}
            field={field}
            localValue={(localValues[field.id] as string[]) || []}
            onValueChange={handleFieldChange}
            disabled={field.disabled || isLoadingOptions}
          />
        );
    }
  };

  return (
    <Box ref={ref} sx={containerSx}>
      {optionsError && (
        <Box
          sx={(theme) => ({
            mt: 1,
            p: 1,
            bgcolor: alpha(theme.palette.warning.main, 0.1),
            borderRadius: 1,
          })}
        >
          <Typography variant="caption" color="warning.dark">
            {optionsErrorMessage || 'Error loading filter options'}
          </Typography>
        </Box>
      )}
      <Box display="flex" flexDirection="column">
        {fields.map(renderField)}
      </Box>
      {!hideActions && (
        <Box display="flex" gap={1} justifyContent="space-between" sx={{ mt: 2, mb: 1 }}>
          <Button
            data-testid="filter_clear_button"
            variant="text"
            color="inherit"
            shape="pill"
            fullWidth
            onClick={handleClear}
          >
            {clearButtonLabel}
          </Button>
          <Button
            variant="contained"
            color="primary"
            shape="pill"
            onClick={handleApply}
            disabled={!hasChanges}
            fullWidth
            data-testid="filter_apply_button"
          >
            {applyButtonLabel}
          </Button>
        </Box>
      )}
    </Box>
  );
});

FilterForm.displayName = 'FilterForm';
